package fr.manyverse.app.infrastructure.image

import fr.manyverse.app.AppLogger
import fr.manyverse.app.domain.error.ImageError
import fr.manyverse.app.domain.error.UnreadableImageError
import fr.manyverse.app.domain.model.ImageData
import kotlinx.coroutines.CancellationException

private const val TAG = "ImageOptimizer"

/**
 * Classe l'échec d'une optimisation d'image et le remonte à Sentry, pour toutes les plateformes.
 *
 * - [UnreadableImageError], levée par la plateforme quand le décodage échoue : l'utilisateur doit
 *   choisir une autre image. Remontée quand même, car le type MIME a déjà été accepté par
 *   `ImageValidator` : un fichier valide refusé révélerait un décodeur incomplet.
 * - [ImageError] pour tout le reste (encodage WebP, mémoire insuffisante…) : un défaut de l'app.
 *
 * Seuls le type MIME et la taille sont loggés, jamais le nom du fichier choisi par l'utilisateur.
 */
internal fun Result<ImageData>.classifyOptimizationFailure(image: ImageData): Result<ImageData> =
    recoverCatching { e ->
        if (e is CancellationException) throw e
        val failure = when (e) {
            is UnreadableImageError, is ImageError -> e
            else -> ImageError("Image optimization failed: ${e.message}", e)
        }
        AppLogger.error(
            TAG,
            "Optimisation d'image impossible (${image.mimeType}, ${image.bytes.size} octets)",
            failure
        )
        throw failure
    }
